/**
 * Immutable History - Append-only event log for all schema mutations.
 *
 * Every change to the NuvraSchema is recorded as an immutable event.
 * Supports time-travel, replay, and re-render from any historical state.
 *
 * History is deterministic, serializable, and cloud-sync ready.
 */

import type { NuvraSchema } from "../schema/nuvraSchema";
import { computeFingerprint, createBlankSchema } from "../schema/nuvraSchema";
import { eventBus } from "@/runtime/eventBus";
import { logger } from "@/system/logger";

/* =========================================================
🔥 ENV GUARD (SERVER SAFE)
========================================================= */

const isBrowser = typeof window !== "undefined" && typeof localStorage !== "undefined";

/* =========================================================
🔥 MUTATION TYPES
========================================================= */

export type MutationType =
  | "schema:init"
  | "page:add"
  | "page:remove"
  | "page:update"
  | "component:add"
  | "component:remove"
  | "component:update"
  | "model:add"
  | "model:remove"
  | "model:update"
  | "workflow:add"
  | "workflow:remove"
  | "workflow:update"
  | "action:add"
  | "action:remove"
  | "capability:add"
  | "capability:remove"
  | "design:update"
  | "meta:update"
  | "bulk:import"
  /**
   * Phase 3C: canonical repair mutation - every self-healing store change
   * is recorded here. source is always "system". Advances fingerprint.
   */
  | "system:repair"
  /**
   * Phase 4A: canonical mutation applied during a scoped regeneration session.
   * source is always "system". Only permitted while a regeneration lock is held.
   * All other mutation types are blocked during an active lock.
   */
  | "system:regeneration"
  /**
   * E-4B-1: atomic security remediation - all structural changes for a single
   * vulnerability fix (pages, models, workflows, field mutations) are applied in
   * one mutator and recorded as one history entry. source is always "system".
   * Does NOT bypass the regeneration lock; blocked fixes are auto-queued by the
   * Phase 4C mutation queue and applied immediately after lock release.
   */
  | "security:fix"
  /**
   * Proposal-only record - created when self-healing detects an issue and
   * generates a repair plan, but execution is suspended (governance constraint).
   * previousFingerprint and newFingerprint are both "" since no schema mutation occurred.
   * source is always "system".
   */
  | "healing:proposal"
  /**
   * Phase 2.0 schema record migration - recorded when schemaMigration.ts migrates
   * one or more typed Phase 2.0 records (NuvraSite, NuvraPage, etc.) to the current
   * SchemaVersionManifest. previousFingerprint and newFingerprint are version stamps
   * computed by computeVersionStamp(). source is always "system".
   */
  | "schema:migration";

/* =========================================================
🔥 EVENT STRUCTURE
========================================================= */

export interface MutationEvent {
  /** Unique, monotonically increasing event ID */
  id: number;

  /** Event type */
  type: MutationType;

  /** Schema fingerprint BEFORE this mutation */
  previousFingerprint: string;

  /** Schema fingerprint AFTER this mutation */
  newFingerprint: string;

  /** The mutation payload - what changed */
  payload: Record<string, unknown>;

  /** Who/what caused this mutation */
  source: "user" | "ai" | "system" | "import";

  /** Timestamp (ms since epoch) */
  timestamp: number;

  /** Optional human-readable description */
  description?: string;
}

/* =========================================================
🔥 HISTORY STORE
========================================================= */

const MAX_EVENTS = 5000;

class ImmutableHistory {
  private events: MutationEvent[] = [];
  private nextId = 1;
  private projectId: string | null = null;

  /* =========================================================
  🔥 INIT
  ========================================================= */

  init(projectId: string): void {
    this.projectId = projectId;
    this.loadFromStorage();
  }

  /* =========================================================
  🔥 RECORD EVENT
  ========================================================= */

  record(
    type: MutationType,
    previousFingerprint: string,
    newFingerprint: string,
    payload: Record<string, unknown>,
    source: MutationEvent["source"] = "user",
    description?: string
  ): MutationEvent {
    const event: MutationEvent = {
      id: this.nextId++,
      type,
      previousFingerprint,
      newFingerprint,
      payload,
      source,
      timestamp: Date.now(),
      description,
    };

    this.events.push(event);

    if (this.events.length > MAX_EVENTS) {
      this.events = this.events.slice(-MAX_EVENTS);
    }

    this.persistToStorage();

    logger.debug("History event recorded", {
      id: event.id,
      type: event.type,
    });

    return event;
  }

  /* =========================================================
  🔥 QUERY
  ========================================================= */

  getEvents(filter?: {
    type?: MutationType;
    source?: MutationEvent["source"];
    since?: number;
  }): MutationEvent[] {
    let result = [...this.events];

    if (filter?.type) {
      result = result.filter((e) => e.type === filter.type);
    }

    if (filter?.source) {
      result = result.filter((e) => e.source === filter.source);
    }

    // ✅ FIXED TS18048 (safe check)
    if (typeof filter?.since === "number") {
      result = result.filter((e) => e.timestamp >= filter.since!);
    }

    return result;
  }

  length(): number {
    return this.events.length;
  }

  recent(count: number = 20): MutationEvent[] {
    return this.events.slice(-count);
  }

  fingerprintAt(eventId: number): string | null {
    const event = this.events.find((e) => e.id === eventId);
    return event?.newFingerprint ?? null;
  }

  replayTo(eventId: number): MutationEvent[] {
    const idx = this.events.findIndex((e) => e.id === eventId);
    if (idx === -1) return [];
    return this.events.slice(0, idx + 1);
  }

  /* =========================================================
  🔥 EXPORT / IMPORT
  ========================================================= */

  export(): { projectId: string | null; events: MutationEvent[] } {
    return {
      projectId: this.projectId,
      events: [...this.events],
    };
  }

  import(data: { events: MutationEvent[] }): void {
    if (!Array.isArray(data.events)) {
      logger.warn("Invalid history import data");
      return;
    }

    this.events = [...data.events];
    this.nextId =
      this.events.length > 0
        ? Math.max(...this.events.map((e) => e.id)) + 1
        : 1;

    this.persistToStorage();
  }

  /* =========================================================
  🔥 CLEAR
  ========================================================= */

  clear(): void {
    this.events = [];
    this.nextId = 1;

    if (this.projectId && isBrowser) {
      try {
        localStorage.removeItem(`nuvra-history-${this.projectId}`);
      } catch {
        /* ignore */
      }
    }
  }

  /* =========================================================
  🔥 PERSISTENCE (BROWSER SAFE)
  ========================================================= */

  private persistToStorage(): void {
    if (!this.projectId || !isBrowser) return;

    try {
      const data = JSON.stringify({
        events: this.events,
        nextId: this.nextId,
      });

      localStorage.setItem(`nuvra-history-${this.projectId}`, data);
    } catch {
      logger.warn("Failed to persist history - storage may be full");
    }
  }

  private loadFromStorage(): void {
    if (!this.projectId || !isBrowser) return;

    try {
      const raw = localStorage.getItem(`nuvra-history-${this.projectId}`);
      if (!raw) return;

      const data = JSON.parse(raw);

      if (Array.isArray(data?.events)) {
        this.events = data.events;
        this.nextId = typeof data.nextId === "number"
          ? data.nextId
          : this.events.length + 1;
      }
    } catch {
      logger.warn("Failed to load history from storage");
    }
  }
}

/* =========================================================
🔥 SINGLETON
========================================================= */

export const immutableHistory = new ImmutableHistory();